import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { key: "modules", label: "Moduli" },
  { key: "hosting", label: "Hosting" },
  { key: "integrations", label: "Integrazioni" },
  { key: "addons", label: "Add-on" },
  { key: "bundles", label: "Bundle" },
];

const INT_CATS = ["marketplace", "ecommerce", "corrieri", "erp", "fatturazione", "fornitori", "logistica"];

const empty = {
  name: "", description: "", price: 0, price_note: "", price_type: "monthly",
  features: [], channels: "", volume: "", includes: "",
  category: "marketplace", integration_type: "standard", logo: "",
  active: true, order: 0, partner_discount_percent: "", bundle_discount_percent: 0,
};

export default function AdminCatalog() {
  const [catalog, setCatalog] = useState({ modules: [], hosting: [], addons: [], bundles: [], integrations: [] });
  const [kind, setKind] = useState("modules");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ marketplace_extra_price: 119, integration_standard_price: 399, integration_custom_price: 500, partner_discount_percent: 0 });

  const load = async () => {
    const { data } = await api.get("/catalog");
    setCatalog(data);
    if (data.settings) setSettings(data.settings);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/settings", {
        marketplace_extra_price: Number(settings.marketplace_extra_price),
        integration_standard_price: Number(settings.integration_standard_price),
        integration_custom_price: Number(settings.integration_custom_price),
        partner_discount_percent: Number(settings.partner_discount_percent) || 0,
        module_discount_tiers: (settings.module_discount_tiers || [])
          .map((t) => ({ min_modules: Number(t.min_modules), percent: Number(t.percent) }))
          .filter((t) => t.min_modules > 0),
      });
      setSettings(data);
      toast.success("Prezzi globali aggiornati");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addTier = () => setSettings({ ...settings, module_discount_tiers: [...(settings.module_discount_tiers || []), { min_modules: 2, percent: 5 }] });
  const updateTier = (i, key, val) => setSettings({ ...settings, module_discount_tiers: settings.module_discount_tiers.map((t, idx) => idx === i ? { ...t, [key]: val } : t) });
  const removeTier = (i) => setSettings({ ...settings, module_discount_tiers: settings.module_discount_tiers.filter((_, idx) => idx !== i) });

  const openNew = () => { setEditing({ ...empty }); setOpen(true); };
  const openEdit = (item) => {
    setEditing({ ...empty, ...item, partner_discount_percent: item.partner_discount_percent == null ? "" : item.partner_discount_percent, bundle_discount_percent: item.bundle_discount_percent || 0 });
    setOpen(true);
  };

  const save = async () => {
    const pdp = editing.partner_discount_percent;
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      order: Number(editing.order) || 0,
      partner_discount_percent: (pdp === "" || pdp === null || pdp === undefined) ? null : Number(pdp),
      bundle_discount_percent: Number(editing.bundle_discount_percent) || 0,
      features: Array.isArray(editing.features)
        ? editing.features
        : String(editing.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing.id) await api.put(`/catalog/${kind}/${editing.id}`, payload);
      else await api.post(`/catalog/${kind}`, payload);
      toast.success("Salvato");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    await api.delete(`/catalog/${kind}/${item.id}`);
    toast.success("Eliminato");
    load();
  };

  const itemPriceLabel = (item, kindKey) => {
    if (kindKey === "integrations") {
      if (item.category === "marketplace") return euro(settings.marketplace_extra_price) + "/mese*";
      let p = item.price;
      if (!p) {
        const t = item.integration_type || "standard";
        p = t === "custom" ? settings.integration_custom_price : t === "included" ? 0 : settings.integration_standard_price;
      }
      return euro(p) + " una tantum";
    }
    if (item.price_type === "onrequest") return item.price_note || "Su richiesta";
    return euro(item.price);
  };

  const featuresText = Array.isArray(editing?.features) ? editing.features.join("\n") : editing?.features || "";

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-3xl font-extrabold text-navy tracking-tight">Catalogo & Prezzi</h1>
        <Button onClick={openNew} data-testid="add-catalog-item-button" className="gap-2 font-bold">
          <Plus size={18} /> Aggiungi
        </Button>
      </div>
      <p className="text-muted-foreground mb-8">Gestisci moduli, hosting, integrazioni, marketplace, add-on e bundle mostrati nel configuratore.</p>

      <div className="bg-white border border-border rounded-xl p-6 mb-8">
        <h2 className="font-extrabold text-navy mb-1">Prezzi globali</h2>
        <p className="text-sm text-muted-foreground mb-4">Applicati al configuratore per marketplace aggiuntivi e integrazioni.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Marketplace aggiuntivo (EUR/mese)</Label>
            <Input type="number" value={settings.marketplace_extra_price} data-testid="setting-marketplace-price"
              onChange={(e) => setSettings({ ...settings, marketplace_extra_price: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Integrazione standard (una tantum)</Label>
            <Input type="number" value={settings.integration_standard_price} data-testid="setting-standard-price"
              onChange={(e) => setSettings({ ...settings, integration_standard_price: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Integrazione custom (una tantum)</Label>
            <Input type="number" value={settings.integration_custom_price} data-testid="setting-custom-price"
              onChange={(e) => setSettings({ ...settings, integration_custom_price: e.target.value })} className="mt-1.5" />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="font-bold text-navy mb-1">Sconto partner riservato alle agenzie</h3>
          <p className="text-sm text-muted-foreground mb-3">Percentuale generale di sconto sul listino applicata al prezzo che l'agenzia paga a Bonsai (prezzo partner). Puoi sovrascriverla per singolo prodotto/bundle. Mai visibile al cliente.</p>
          <div className="max-w-xs">
            <Label>Sconto partner generale (%)</Label>
            <Input type="number" value={settings.partner_discount_percent ?? 0} data-testid="setting-partner-discount"
              onChange={(e) => setSettings({ ...settings, partner_discount_percent: e.target.value })} className="mt-1.5" />
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-navy">Sconti automatici per numero di moduli</h3>
            <Button variant="outline" size="sm" onClick={addTier} data-testid="add-tier-button" className="gap-1.5 font-bold"><Plus size={14} /> Soglia</Button>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Lo sconto si applica automaticamente al totale dei moduli in base a quanti moduli vengono selezionati.</p>
          <div className="space-y-2">
            {(settings.module_discount_tiers || []).length === 0 && <p className="text-sm text-muted-foreground">Nessuna soglia. Aggiungine una.</p>}
            {(settings.module_discount_tiers || []).map((t, i) => (
              <div key={i} data-testid={`tier-row-${i}`} className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-navy">Da</span>
                <Input type="number" value={t.min_modules} data-testid={`tier-min-${i}`} onChange={(e) => updateTier(i, "min_modules", e.target.value)} className="w-20" />
                <span className="text-sm text-navy">moduli →</span>
                <Input type="number" value={t.percent} data-testid={`tier-percent-${i}`} onChange={(e) => updateTier(i, "percent", e.target.value)} className="w-20" />
                <span className="text-sm text-navy">% sconto</span>
                <button onClick={() => removeTier(i)} data-testid={`tier-remove-${i}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200 ml-1"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={saveSettings} data-testid="save-settings-button" className="mt-4 font-bold">Salva prezzi globali</Button>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="mb-6">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`tab-${k.key}`}>{k.label}</TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key}>
            <div className="space-y-3">
              {catalog[k.key].length === 0 && <p className="text-muted-foreground">Nessun elemento.</p>}
              {catalog[k.key].map((item) => (
                <div key={item.id} data-testid={`catalog-item-${item.id}`} className="flex items-center justify-between gap-4 bg-white border border-border rounded-xl p-4">
                  <div className="min-w-0">
                    <p className="font-bold text-navy">{item.name} {!item.active && <span className="text-xs text-destructive">(disattivo)</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.description || item.volume}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-extrabold text-navy whitespace-nowrap">
                      {itemPriceLabel(item, k.key)}
                    </span>
                    <button onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Pencil size={16} /></button>
                    <button onClick={() => remove(item)} data-testid={`delete-item-${item.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifica" : "Nuovo"} elemento</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} data-testid="item-name-input" onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Textarea value={editing.description} rows={2} data-testid="item-description-input" onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prezzo (EUR)</Label>
                  <Input type="number" value={editing.price} data-testid="item-price-input" onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Tipo prezzo</Label>
                  <Select value={editing.price_type} onValueChange={(v) => setEditing({ ...editing, price_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="item-pricetype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensile</SelectItem>
                      <SelectItem value="oneoff">Una tantum</SelectItem>
                      <SelectItem value="onrequest">Su richiesta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nota prezzo</Label>
                  <Input value={editing.price_note} data-testid="item-pricenote-input" onChange={(e) => setEditing({ ...editing, price_note: e.target.value })} placeholder="es. + crediti AI" className="mt-1.5" />
                </div>
                <div>
                  <Label>Ordine</Label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              {(kind === "integrations") && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Categoria</Label>
                      <Select value={editing.category || "marketplace"} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="item-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {INT_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {editing.category !== "marketplace" && (
                      <div>
                        <Label>Tipo integrazione</Label>
                        <Select value={editing.integration_type || "standard"} onValueChange={(v) => setEditing({ ...editing, integration_type: v })}>
                          <SelectTrigger className="mt-1.5" data-testid="item-inttype-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                            <SelectItem value="included">Inclusa (gratis)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Logo URL (opzionale)</Label>
                    <Input value={editing.logo} data-testid="item-logo-input" onChange={(e) => setEditing({ ...editing, logo: e.target.value })} placeholder="https://..." className="mt-1.5" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Marketplace: fatturato per unita oltre la prima (prezzo da "Prezzi globali"). Integrazioni: costo una tantum in base al tipo. Lascia prezzo 0 per usare i prezzi globali, oppure imposta un prezzo specifico.
                  </p>
                </>
              )}
              {(kind === "hosting") && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Volume ordini</Label>
                    <Input value={editing.volume} onChange={(e) => setEditing({ ...editing, volume: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Incluso</Label>
                    <Input value={editing.includes} onChange={(e) => setEditing({ ...editing, includes: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
              )}
              {(kind === "modules" || kind === "bundles") && (
                <>
                  <div>
                    <Label>Canali / Integrazioni</Label>
                    <Input value={editing.channels} onChange={(e) => setEditing({ ...editing, channels: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Funzionalita (una per riga)</Label>
                    <Textarea value={featuresText} rows={4} onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })} className="mt-1.5" />
                  </div>
                </>
              )}
              {kind === "bundles" && (
                <div>
                  <Label>Sconto bundle (%) — sempre visibile al cliente</Label>
                  <Input type="number" value={editing.bundle_discount_percent ?? 0} data-testid="item-bundle-discount-input"
                    onChange={(e) => setEditing({ ...editing, bundle_discount_percent: e.target.value })} className="mt-1.5" placeholder="es. 10" />
                  <p className="text-xs text-muted-foreground mt-1">Sconto applicato al prezzo del bundle e mostrato in offerta e PDF.</p>
                </div>
              )}
              <div>
                <Label>Sconto partner override (%) — solo agenzia</Label>
                <Input type="number" value={editing.partner_discount_percent} data-testid="item-partner-discount-input"
                  onChange={(e) => setEditing({ ...editing, partner_discount_percent: e.target.value })} className="mt-1.5" placeholder="Vuoto = usa sconto generale" />
                <p className="text-xs text-muted-foreground mt-1">Sovrascrive lo sconto partner generale per questo elemento. Lascia vuoto per usare quello generale. Mai visibile al cliente.</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="item-active-switch" />
                <Label>Attivo (visibile nel configuratore)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-item-button" className="font-bold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
